/**
 * Shared API error → user-facing snackbar copy.
 * Prefer server JSON message/error; otherwise status/network reasons; compose with an action fallback.
 */

/** Prefer JSON { message } / { error }; ignore nginx HTML bodies (e.g. 413 pages). */
export function extractServerMessage(data) {
  if (data && typeof data === 'object') {
    if (typeof data.message === 'string' && data.message.trim()) {
      return data.message.trim()
    }
    if (typeof data.error === 'string' && data.error.trim()) {
      return data.error.trim()
    }
  }
  if (typeof data === 'string') {
    const trimmed = data.trim()
    if (!trimmed || trimmed.startsWith('<') || /request entity too large/i.test(trimmed)) {
      return ''
    }
    return trimmed
  }
  return ''
}

function stripTrailingPeriod(s) {
  return String(s || '').replace(/\.+$/, '').trim()
}

/** Short/machine-like server errors get a fallback prefix; full sentences stand alone. */
export function looksLikeFullSentence(message) {
  const m = String(message || '').trim()
  if (!m) return false
  if (m.length >= 48) return true
  if (/[.!?]$/.test(m)) return true
  if (/\s/.test(m) && m.length >= 24) return true
  return false
}

function reasonFromStatus(err) {
  const status = err?.response?.status
  if (!err?.response && (err?.code === 'ERR_NETWORK' || err?.message === 'Network Error')) {
    return 'Network error. Check your connection and try again.'
  }
  if (status === 401) {
    return 'Session expired. Sign in and try again.'
  }
  if (status === 403) {
    return 'You do not have permission to do that.'
  }
  if (status === 404) {
    return 'Not found. Refresh the page and try again.'
  }
  if (status === 413) {
    return 'That file or request is too large.'
  }
  if (status >= 400 && status < 500) {
    return 'Check your input and try again.'
  }
  if (status >= 500) {
    return 'Server error. Try again in a moment.'
  }
  return 'Try again in a moment.'
}

/**
 * @param {unknown} err axios-style error
 * @param {string} actionFallback e.g. "Could not load jobs."
 */
export function messageForApiError(err, actionFallback = 'Something went wrong.') {
  const fallback = String(actionFallback || 'Something went wrong.').trim() || 'Something went wrong.'
  const serverMessage = extractServerMessage(err?.response?.data)

  if (serverMessage) {
    if (looksLikeFullSentence(serverMessage)) {
      return serverMessage
    }
    return `${stripTrailingPeriod(fallback)}: ${serverMessage}`
  }

  const reason = reasonFromStatus(err)
  if (!fallback) return reason
  return `${stripTrailingPeriod(fallback)}: ${reason}`
}
